/**
 * Created by Mike Naughton on 10/17/17.
 *
 * A set of operations applicable to both MapLayerBase and MapServiceBase instances
 */

import $ from "jquery";
import _ from "underscore";
import async from "async";
import Operation from "core/models/Operation";
import OperationSet from "core/models/OperationSet";
import GeoUtils from "app/utils/GeoUtils";
import Field from "core/models/Field";
import ModelDirectoryView from "app/views/map/ModelDirectoryView";
import FormInputView from "core/views/FormInputView";
import auth from "app/auth";
import ErrorUtils from "app/utils/ErrorUtils";
import modals from "core/views/modals";
import mapContext from "app/views/map/MapContext";

export default new OperationSet([
  new Operation({
    id: "zoom-to-item",
    text: "Zoom To",
    icon: "fullscreen",
    action: function (model) {
      var deferred = new $.Deferred();
      var modelExtent = GeoUtils.getExtent(model);

      if (modelExtent) {
        var expandedExtentForView = GeoUtils.expandExtentForView(modelExtent);
        var defaultError = "Error occurred setting extent of map";

        async.retry(
          { times: 5, interval: 500 },
          function (cb) {
            var promise = mapContext
              .getMap()
              .setExtent(expandedExtentForView, true);
            promise.then(function () {
              cb();
            });
            if (promise.fail) {
              promise.fail(function (err) {
                cb(err || defaultError);
              });
            } else if (promise.catch) {
              promise.catch(function (err) {
                cb(err || defaultError);
              });
            }
          },
          function (err) {
            if (err) {
              return deferred.reject(err);
            }

            deferred.resolve(model);
          }
        );
      } else {
        deferred.resolve(model);
      }

      return deferred.promise();
    },
  }),

  new Operation({
    id: "pan-to-item",
    text: "Pan To",
    icon: "map-marker",
    action: function (model) {
      var deferred = new $.Deferred();

      mapContext
        .getMap()
        .centerAt(GeoUtils.getCenter(model))
        .then(
          function () {
            deferred.resolve(model);
          },
          function (err) {
            deferred.reject(err);
          }
        );

      return deferred.promise();
    },
  }),

  new Operation({
    id: "show-item",
    text: "Show",
    icon: "eye-open",
    action: function (model) {
      model.show();

      return Operation.resolve(model);
    },
  }),

  new Operation({
    id: "hide-item",
    text: "Hide",
    icon: "eye-close",
    action: function (model) {
      model.hide();

      return Operation.resolve(model);
    },
  }),

  new Operation({
    id: "view-item",
    text: "View Properties",
    icon: "sunglasses",
    action: function (model) {
      var deferred = new $.Deferred();

      mapContext.display(model);
      mapContext
        .getOperation("clear-selection")
        .action()
        .done(function () {
          deferred.resolve(model);
        })
        .fail(function (err) {
          deferred.reject(err);
        });

      return deferred.promise();
    },
  }),

  new Operation({
    id: "set-selectable",
    icon: "unchecked",
    text: "Set Selectable",
    action: function (model) {
      model.setSelectable(true);
      mapContext.display(model);

      return Operation.resolve(model);
    },
    available: function (model) {
      return model && !model.isSelectable();
    },
  }),

  new Operation({
    id: "set-unselectable",
    icon: "check",
    text: "Set Unselectable",
    action: function (model) {
      model.setSelectable(false);
      mapContext.display(model);

      return Operation.resolve(model);
    },
    available: function (model) {
      return model && model.isSelectable();
    },
  }),

  new Operation({
    id: "view-item-files",
    text: "View Files",
    icon: "folder-open",
    action: function (model) {
      var deferred = new $.Deferred();

      var fields = model.getFields();
      var layers = model.layers || [model];
      var allModels = [];

      layers = _.indexBy(layers, function (layer) {
        return layer.getWrappedModel().id;
      });

      model
        .loadFiles()
        .done(function (layerMap) {
          async.each(
            _.keys(layerMap),
            function (layerId, cb) {
              var layer = layers[layerId];
              var documentMap = layerMap[layerId];

              async.map(
                _.keys(documentMap),
                function (modelId, cb) {
                  layer
                    .loadModel(modelId)
                    .done(function (model) {
                      cb(null, model);
                    })
                    .fail(function (err) {
                      cb(err);
                    });
                },
                function (err, models) {
                  if (err) {
                    cb(err);
                  }
                  allModels = allModels.concat(models);
                  cb();
                }
              );
            },
            function (err) {
              if (err) {
                return deferred.reject(err);
              }

              fields.unshift(
                new Field({
                  id: "id",
                  name: "id",
                  label: "",
                  editor: "custom",
                  editable: false,
                  ViewerView: {
                    template: _.template(
                      '<a href="<%-url%>" class="response-select" data-feature="<%-value%>"><%-text%></a>'
                    ),

                    serialize: function (options) {
                      return {
                        url: "#",
                        value: options.value,
                        text: "Select Object",
                      };
                    },

                    getControlValue: function () {
                      return this.$("a").data("feature");
                    },

                    setControlValue: function () {
                      this.render();
                    },
                  },
                })
              );
              var fileView = new ModelDirectoryView({
                models: allModels,
                fields: fields,
                title: "All Models with Files",
                featureLayer: model.getWrappedModel(),
              });

              fileView.render({ height: 450, width: 450 });
              fileView.view.$el.on("click", ".response-select", function (evt) {
                var modelId = $(evt.target).data("feature");
                var selectedModels = _.find(allModels, function (model) {
                  return model && model.id == modelId;
                });

                var originalLayer = _.find(
                  model.layers || [model],
                  function (model) {
                    return (
                      model.featureLayer.id == selectedModels.get("dataType")
                    );
                  }
                );

                if (originalLayer) {
                  selectedModels.originalLayer = originalLayer;
                }
                selectedModels = [selectedModels];

                mapContext
                  .getOperation("show-geometry-details")
                  .action(selectedModels)
                  .then(function () {
                    return mapContext
                      .getOperation("highlight-geometry")
                      .action(selectedModels);
                  })
                  .fail(
                    ErrorUtils.errorHandler({
                      activity: "selecting the object.",
                    })
                  );
              });
              fileView.navigateStart();
              deferred.resolve(model);
            }
          );
        })
        .fail(function (err) {
          deferred.reject(err);
        });

      return deferred.promise();
    },
    available: function (model) {
      return model && model.hasFiles();
    },
  }),

  new Operation({
    id: "download-item",
    text: "Download",
    icon: "download",
    action: function (model) {
      var wrappedModel = model.getWrappedModel();
      var url = _.result(wrappedModel, "url");
      //determine the possible output formats and add a default so there's something to indicate no selection
      var outputFormats = _.filter(
        [
          {
            value: "kml",
            text: "Google KML",
          },
          {
            value: "shp",
            text: "ESRI Shapefile",
          },
          {
            value: "json",
            text: "JSON",
          },
          {
            value: "csv",
            text: "CSV",
          },
          {
            value: "xls",
            text: "Excel file",
          },
        ],
        function (outputFormat) {
          return wrappedModel.canExportTo(outputFormat.value);
        }
      );

      outputFormats.unshift({
        value: "",
        text: "Select a Format",
      });

      var field = new Field({
        id: "exportType",
        name: "exportType",
        label: "Download Format",
        values: outputFormats,
      });
      var inputModel = {
        exportType: "",
      };
      var inputView = new FormInputView({
        field: field,
        model: inputModel,
      });
      var linkArea = $("<div></div>");
      var linkTemplate = _.template(
        '<a href="' +
          url +
          '<%-ext%>?token=<%-token%>" target="_blank">Download</a>'
      );
      var extensions = {
        kmz: ".kmz",
        kml: ".kml",
        shp: ".zip",
        json: ".json",
        xls: ".xlsx",
        csv: ".csv",
      };

      inputView.render();
      inputView.on("change", function (value) {
        linkArea.html(
          linkTemplate({
            ext: extensions[value],
            token: auth.token,
          })
        );
      });

      var content = $();

      content = content.add(inputView.$el);
      content = content.add(linkArea);

      modals.modal("Download Data", content);

      return Operation.resolve(model);
    },
    available: function (model) {
      return model && model.canExport() && model.getWrappedModel() != null;
    },
  }),
]);



//////////////////
// WEBPACK FOOTER
// ./public/js/app/operations/map/FeatureContainerOperations.js
// module id = 915
// module chunks = 1